import { FC } from "react";

import {
  SearchPanel,
  type SearchScope,
} from "@tsmono/inspect-components/transcript-search";

import { useAppConfig } from "../../../../app_config";

import {
  useInspectSearchApi,
  useInspectSearchModelHistory,
  useInspectSearchNavigation,
  useInspectSearchPanelState,
  type InspectSearchContext,
} from "./inspectSearchAdapters";
import { SearchScoutUnavailable } from "./SearchScoutUnavailable";

interface SearchPanelSlotProps {
  scope: SearchScope;
  context: InspectSearchContext;
  onClose: () => void;
}

/**
 * Renders the shared SearchPanel, or an "install Inspect Scout" notice when
 * the backend reports scout isn't installed (search endpoints proxy to it).
 * Callers obtain `context` from `useInspectSearchContext(sample)`, which
 * encapsulates the "is this sample searchable + can we build URLs?" decision.
 */
export const SearchPanelSlot: FC<SearchPanelSlotProps> = ({
  scope,
  context,
  onClose,
}) => {
  const appConfig = useAppConfig();

  if (appConfig.scout_version == null) {
    return <SearchScoutUnavailable onClose={onClose} />;
  }

  return (
    <ConnectedSearchPanel scope={scope} context={context} onClose={onClose} />
  );
};

/** Bundles the four adapter hooks and renders the shared SearchPanel. */
const ConnectedSearchPanel: FC<SearchPanelSlotProps> = ({
  scope,
  context,
  onClose,
}) => {
  const { transcriptId, logFile, logPath, sampleId, sampleEpoch } = context;
  const api = useInspectSearchApi(logFile, transcriptId);
  const stateController = useInspectSearchPanelState({
    scope,
    logFile,
    transcriptId,
  });
  const navigation = useInspectSearchNavigation({
    logPath,
    sampleId,
    sampleEpoch,
  });
  const modelHistory = useInspectSearchModelHistory();

  if (!api) return null;

  return (
    <SearchPanel
      scope={scope}
      api={api}
      stateController={stateController}
      navigation={navigation}
      modelHistory={modelHistory}
      onClose={onClose}
    />
  );
};
